// How to add another LinkedIn account: one Chrome profile per account, the extension in each,
// and one explicit "Connect @handle" click in the extension for a brand-new account.

import { Callout, Card, buttonClass } from "@ml/ui";
import { ArrowUpRight, Download, Info } from "lucide-react";
import { Link } from "react-router";
import { ExtensionGlyph } from "../IgGlyph";
import "./ig-workspaces.css";

export const ADD_ACCOUNT_ANCHOR = "add-account";

export function AddIgAccountGuide({ used, limit }: { used: number; limit: number }) {
  const atLimit = used >= limit;
  return (
    <Card
      id={ADD_ACCOUNT_ANCHOR}
      className="gfp-ws-guide"
      title={atLimit ? "You're at your plan's limit" : "Add another LinkedIn account"}
      subtitle={
        atLimit
          ? `Your plan runs ${limit} LinkedIn ${limit === 1 ? "account" : "accounts"}, and all of them are taken.`
          : "Each LinkedIn account runs in its own Chrome profile. Five steps, about three minutes."
      }
    >
      {atLimit ? (
        <Callout tone="neutral" title="Need room for another account?">
          Ask your reseller or support to review your LinkedIn account allowance. Disconnecting an account permanently removes its Magically Linked data, so use that option only when you intend to remove it.
        </Callout>
      ) : null}
      <ol className="gfp-ws-guide__steps">
        <li>
          <b>Make a Chrome profile for the account.</b> Click your profile picture at the top right of Chrome, then{" "}
          <i>Add</i>. One profile per LinkedIn account keeps their logins apart.
        </li>
        <li>
          <b>Install the Magically Linked extension in that profile.</b>{" "}
          <Link to="/extension" className="gfp-ws-guide__link">
            <Download size={13} aria-hidden="true" /> Get the extension
          </Link>
        </li>
        <li>
          <b>Sign in to Magically Linked in that profile.</b> Open app.magicallylinked.com there and log in; the extension signs in
          with you automatically.{" "}
          <Link to="/connect-extension" className="gfp-ws-guide__link">
            <ExtensionGlyph size={13} /> Extension setup
          </Link>
        </li>
        <li>
          <b>Log into the LinkedIn account</b> on{" "}
          <a href="https://www.linkedin.com/" target="_blank" rel="noreferrer" className="gfp-ws-guide__link">
            www.linkedin.com <ArrowUpRight size={13} aria-hidden="true" />
          </a>{" "}
          in that profile.
        </li>
        <li>
          <b>Open the extension and click "Connect @handle".</b> It shows up here within a minute
          {atLimit ? " once you have a free slot" : ""}.
        </li>
      </ol>
      <p className="gfp-ws-guide__note">
        <Info size={14} aria-hidden="true" />
        <span>
          Accounts you've already connected are picked up automatically in any profile where they're logged in, no click
          needed. Keep each profile open to run those accounts concurrently. Only one browser runs an account at a time; any other browser logged into it stands by.
        </span>
      </p>
      {atLimit ? null : (
        <div className="gfp-ws-guide__actions">
          <Link to="/extension" className={buttonClass({ variant: "secondary", size: "sm" })}>
            <Download size={15} aria-hidden="true" /> Get the extension
          </Link>
        </div>
      )}
    </Card>
  );
}
